import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { twMerge } from 'tailwind-merge';

import {
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  CircleQuestionMark,
  X,
} from '@repo/media/lucide';

import { Button } from '@repo/components/button';
import { ButtonLink } from '@repo/components/button-link';
import { Card } from '@repo/components/card';
import { SIDEBAR_ITEM_ACTIVE_CLASSES } from '@repo/components/sidebar';
import { Tooltip, TooltipTrigger } from '@repo/components/tooltip';

import { EnvironmentIndicator } from '@repo/features/environment-indicator';
import { resolveRuntimeEnvironment } from '@repo/utils/runtime-environment';

import { routesByPath } from '~/config/routes.ts';
import { FeatureGuard } from '~/features/auth/feature-guard.tsx';
import { useFeatureGuard } from '~/features/auth/use-feature-guard.ts';
import { useLayoutStore } from '~/features/layout-base-page/store.ts';
import { useIsSmall } from '~/hooks/use-is-small.ts';
import { useMatchedRoute } from '~/hooks/use-matched-route.ts';
import { queryCurrentUser, queryProjectById, queryProjects } from '~/services/authorization/authorization.ts';

import { AccountButton } from './account-button.tsx';
import { LogoLink } from './logo-link.tsx';

const navigationLevel = {
  organization: [
    { ...routesByPath['/organization/$org'], exact: true },
    routesByPath['/organization/$org/user-mgmt'],
    routesByPath['/organization/$org/api-docs'],
  ],
  project: [
    { ...routesByPath['/project/$project'], exact: true },
    routesByPath['/project/$project/cargoes'],
    routesByPath['/project/$project/declarations'],
    routesByPath['/project/$project/composition/setup'],
    routesByPath['/project/$project/certificates'],
    routesByPath['/project/$project/invoices'],
    routesByPath['/project/$project/user-mgmt'],
  ],
};

export function Sidebar(
  { isOpen, close }:
  { isOpen?: boolean; close: () => void },
) {
  const { sidebarCollapsed, toggleSidebar } = useLayoutStore();
  const isSmall = useIsSmall();
  const environment = resolveRuntimeEnvironment({
    isDev: import.meta.env.DEV,
    appEnvironment: import.meta.env.VITE_APP_ENVIRONMENT,
  });
  const currentPath = useMatchedRoute().path;
  const params = useParams({ strict: false });
  const level = currentPath.startsWith('/project') ? 'project' : 'organization';
  const { data: user } = useQuery(queryCurrentUser());
  const { data: projects = [] } = useQuery(queryProjects(user?.organizationId ?? ''));
  const { data: project } = useQuery(queryProjectById(params.project ?? ''));
  const { hasFeature } = useFeatureGuard();

  if (isSmall && !isOpen) {
    return null;
  }

  const isExpanded = isSmall || !sidebarCollapsed;
  const showOrgLink = level === 'project' && isExpanded && (projects.length > 1 || hasFeature({ key: 'user-management', scope: 'Organization' }));

  return (
    <nav className={twMerge(
      'bg-navigation-bg',
      'flex flex-col overflow-hidden',
      'absolute top-0 left-0 z-[var(--sidebar-z-index)] w-dvw h-dvh',
      'lg:relative lg:z-auto lg:top-auto lg:left-auto lg:size-full',
    )}
    >
      {sidebarCollapsed && !isSmall && (
        <div className="flex shrink-0 flex-col items-center gap-1 pt-6">
          <LogoLink vertical onClick={() => { close(); }} />
          <EnvironmentIndicator visible={environment.visible} label={environment.label} className="text-[10px] px-1.5 py-0" />
        </div>
      )}

      {(isSmall || isExpanded) && (
        <div className={twMerge('flex shrink-0 justify-between items-center gap-4', 'px-3 pt-6 pb-2', 'border-b border-b-transparent')}>
          {isExpanded && (
            <div className="flex items-center gap-2">
              <LogoLink onClick={() => { close(); }} />
              <EnvironmentIndicator visible={environment.visible} label={environment.label} />
            </div>
          )}
          {isSmall && (
            <div className={twMerge('flex items-center gap-4')}>
              <AccountButton />
              <Button
                size="icon-md"
                icon={{ component: <X strokeWidth={1.5} size={30} aria-label="Close menu" /> }}
                action={() => { close(); }}
              />
            </div>
          )}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <div className={twMerge(
          'flex flex-col gap-5',
          'my-4 mx-2',
        )}
        >
          {(showOrgLink && project) && (
            <>
              <div className="flex flex-col gap-1">
                <ButtonLink
                  to="/organization/$org"
                  params={{ org: user?.organizationId ?? '' }}
                  className="justify-start"
                  variant="ghost"
                  color="secondary"
                  icon={{ component: <ArrowLeft size={20} strokeWidth={1.5} /> }}
                  onClick={close}
                  size="sm"
                >
                  Sites
                </ButtonLink>
                <Card
                  className={twMerge(
                    'gap-1 py-2',
                  )}
                >
                  <p className="text font-medium">
                    {project.name}
                  </p>
                </Card>
              </div>
            </>
          ) }
          <div className="flex flex-col gap-1.5">
            {isExpanded && (
              <p className="font-semibold text-xs uppercase tracking-wider text-secondary-700 px-2.5">
                {level === 'organization' ? 'Organization' : 'Site features'}
              </p>
            )}
            <ul
              className={twMerge(
                'flex flex-col gap-1',
              )}
            >
              {navigationLevel[level].map(({ icon: Icon, ...route }) => {
                const isExact = 'exact' in route && route.exact;
                const active = currentPath === route.path
                  || (!isExact && currentPath.startsWith(`${route.path}/`));
                const link = (
                  <ButtonLink
                    to={route.path}
                    params={p => ({ project: p.project ?? '' })}
                    className={twMerge(
                      isExpanded && 'justify-start h-8',
                      active && SIDEBAR_ITEM_ACTIVE_CLASSES,
                    )}
                    onClick={close}
                    icon={{ component: Icon
                      ? <Icon size={isExpanded ? 18 : 24} strokeWidth={1.5} aria-label={route.title} />
                      : <CircleQuestionMark size={isExpanded ? 18 : 24} strokeWidth={1.5} aria-label={route.title} /> }}
                    variant="ghost"
                    color="secondary"
                    size={isExpanded ? 'sm' : 'icon-md'}
                  >
                    {isExpanded && route.title}
                  </ButtonLink>
                );
                return (
                  <FeatureGuard key={route.path} features={route.features}>
                    <li>
                      {isExpanded
                        ? link
                        : (
                            <TooltipTrigger delay={300}>
                              {link}
                              <Tooltip placement="right">{route.title}</Tooltip>
                            </TooltipTrigger>
                          )}
                    </li>
                  </FeatureGuard>
                );
              })}
            </ul>
          </div>
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-1 p-2 w-full">
        {!isSmall && (
          <>
            <div className={twMerge('flex', !isExpanded && 'justify-center')}>
              <AccountButton showName={isExpanded} />
            </div>
            <hr className="border-secondary-200" />
            {sidebarCollapsed
              ? (
                  <div className="flex justify-center">
                    <Button
                      variant="ghost"
                      size="icon-md"
                      color="secondary"
                      action={toggleSidebar}
                      icon={{ component: <ChevronRight size={30} strokeWidth={1.5} aria-label="Expand menu" /> }}
                    />
                  </div>
                )
              : (
                  <Button
                    variant="ghost"
                    size="icon-md"
                    color="secondary"
                    action={toggleSidebar}
                    icon={{ component: <ChevronLeft size={24} strokeWidth={1.5} /> }}
                    className="w-full justify-start"
                  >
                    Collapse menu
                  </Button>
                )}
          </>
        )}
      </div>
    </nav>
  );
}
